import { useEffect, useMemo, useState } from 'react';
import { Card } from '../../../components/ui/Card';
import { FormField, Input } from '../../../components/ui/Form';
import { Button } from '../../../components/ui/Button';
import { useJournalSequences } from '../../../hooks/useJournalSequences';
import { useFiscalYear } from '../../../hooks/useFiscalYear';
import {
  JOURNAL_TYPE_LABELS,
  type JournalSequence,
} from '../../../types/journal-sequences';
import { Hash } from 'lucide-react';

interface DraftConfig {
  prefix: string;
  padding: string; // string pour faciliter l'input contrôlé
}

/**
 * Configuration des séquences de numérotation des pièces (DGI/SYSCOHADA).
 *
 * Les séquences sont créées à l'ouverture de chaque exercice fiscal et au
 * besoin de manière paresseuse côté backend. L'utilisateur ne peut modifier
 * que le préfixe (ex: VTE, ACH) et la largeur de remplissage zéro — jamais
 * le compteur courant, pour préserver la continuité chronologique.
 */
export function JournalSequencesSettings() {
  const { currentFiscalYear } = useFiscalYear();
  const { sequences, loading, saving, error, updateConfiguration } =
    useJournalSequences(currentFiscalYear?.id);

  const [drafts, setDrafts] = useState<Record<string, DraftConfig>>({});

  useEffect(() => {
    const next: Record<string, DraftConfig> = {};
    for (const seq of sequences) {
      next[seq.id] = {
        prefix: seq.prefix,
        padding: String(seq.padding),
      };
    }
    setDrafts(next);
  }, [sequences]);

  const sortedSequences = useMemo(() => {
    const order: Record<string, number> = {
      sales: 1,
      purchases: 2,
      bank: 3,
      cash: 4,
      general: 5,
    };
    return [...sequences].sort(
      (a, b) =>
        (order[a.journalType] ?? 99) - (order[b.journalType] ?? 99),
    );
  }, [sequences]);

  const buildPreview = (sequence: JournalSequence, draft?: DraftConfig) => {
    const prefix = (draft?.prefix ?? sequence.prefix).trim().toUpperCase() || '?';
    const paddingValue = Number(draft?.padding ?? sequence.padding) || 6;
    const next = Number(sequence.currentSequence) + 1;
    return `${prefix}-${sequence.fiscalYearLabel}-${String(next).padStart(
      Math.max(3, Math.min(10, paddingValue)),
      '0',
    )}`;
  };

  const handleSave = async (sequence: JournalSequence) => {
    const draft = drafts[sequence.id];
    if (!draft) return;
    const cleanedPrefix = draft.prefix.trim().toUpperCase();
    const paddingNumber = Number(draft.padding);

    if (!cleanedPrefix) return;
    if (cleanedPrefix === sequence.prefix && paddingNumber === sequence.padding) {
      return; // rien à enregistrer
    }
    await updateConfiguration(sequence.id, {
      prefix: cleanedPrefix,
      padding: paddingNumber,
    });
  };

  if (!currentFiscalYear) {
    return (
      <Card title="Numérotation des pièces (DGI/SYSCOHADA)" icon={Hash}>
        <p className="text-sm text-muted-foreground">
          Aucun exercice fiscal courant. Créez ou activez un exercice pour
          configurer les séquences de numérotation.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Numérotation des pièces (DGI/SYSCOHADA)" icon={Hash}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Chaque type de journal possède sa propre séquence par exercice. Le
          numéro de pièce suit le format{' '}
          <code className="px-1 py-0.5 bg-muted rounded text-xs">
            PRÉFIXE-EXERCICE-NUMÉRO
          </code>{' '}
          (exemple : <code className="px-1 py-0.5 bg-muted rounded text-xs">VTE-{currentFiscalYear.code}-000042</code>).
          Le compteur n'est pas modifiable pour préserver la chronologie.
        </p>

        {error && (
          <div className="p-3 rounded border border-destructive/30 bg-destructive/5 text-sm text-destructive">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Chargement des séquences…
          </p>
        ) : sortedSequences.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Aucune séquence n'a encore été générée pour cet exercice. Elles
            seront créées automatiquement à la première écriture.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Journal</th>
                  <th className="py-2 pr-4 font-medium">Préfixe</th>
                  <th className="py-2 pr-4 font-medium">Padding</th>
                  <th className="py-2 pr-4 font-medium">Compteur</th>
                  <th className="py-2 pr-4 font-medium">Aperçu prochaine pièce</th>
                  <th className="py-2 pr-4 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedSequences.map((seq) => {
                  const draft = drafts[seq.id];
                  const isDirty =
                    draft &&
                    (draft.prefix.trim().toUpperCase() !== seq.prefix ||
                      Number(draft.padding) !== seq.padding);

                  return (
                    <tr key={seq.id} className="border-b last:border-b-0">
                      <td className="py-3 pr-4 font-medium">
                        {JOURNAL_TYPE_LABELS[seq.journalType] ?? seq.journalType}
                      </td>
                      <td className="py-3 pr-4">
                        <FormField label="" className="mb-0">
                          <Input
                            value={draft?.prefix ?? seq.prefix}
                            maxLength={10}
                            onChange={(e) =>
                              setDrafts((prev) => ({
                                ...prev,
                                [seq.id]: {
                                  prefix: e.target.value,
                                  padding:
                                    prev[seq.id]?.padding ??
                                    String(seq.padding),
                                },
                              }))
                            }
                          />
                        </FormField>
                      </td>
                      <td className="py-3 pr-4 w-20">
                        <FormField label="" className="mb-0">
                          <Input
                            type="number"
                            min={3}
                            max={10}
                            value={draft?.padding ?? String(seq.padding)}
                            onChange={(e) =>
                              setDrafts((prev) => ({
                                ...prev,
                                [seq.id]: {
                                  prefix:
                                    prev[seq.id]?.prefix ?? seq.prefix,
                                  padding: e.target.value,
                                },
                              }))
                            }
                          />
                        </FormField>
                      </td>
                      <td className="py-3 pr-4 tabular-nums">
                        {Number(seq.currentSequence)}
                      </td>
                      <td className="py-3 pr-4 font-mono text-xs">
                        {buildPreview(seq, draft)}
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!isDirty || saving}
                          onClick={() => handleSave(seq)}
                        >
                          Enregistrer
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}
